import { useMemo } from 'react'
import { DonutChartWrapper, type DonutSlice } from '../charts/DonutChart'
import { formatMoney } from '../format'
import { buildDrillRsql } from '../drill'
import { WidgetFrame } from './WidgetFrame'
import { useWidgetData } from './useWidgetData'
import { WIDGET_KEY, type WidgetProps } from './types'
import type { OutcomePathBucket } from '../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.monetizationOutcomes
const MAX_SLICES = 6

/** AC6 — Monetization outcomes. Hero tile 3 of 3. Caps at 6 categories + "other" (design-system.md §5.1). */
export function MonetizationOutcomesWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<OutcomePathBucket[]>(WIDGET_KEY_SELF, filter, skipRefetch)

  const { slices, currency } = useMemo(() => {
    const buckets = data ?? []
    const sorted = [...buckets].sort((a, b) => Number(b.sumValue) - Number(a.sumValue))
    const head = sorted.slice(0, MAX_SLICES)
    const tail = sorted.slice(MAX_SLICES)
    const result: DonutSlice[] = head.map((b) => ({ key: b.path, label: b.label, value: Number(b.sumValue) }))
    if (tail.length > 0) {
      result.push({
        key: 'other',
        label: `Other (${tail.length})`,
        value: tail.reduce((sum, b) => sum + Number(b.sumValue), 0),
      })
    }
    return { slices: result, currency: buckets[0]?.currency ?? 'USD' }
  }, [data])

  const frameState = status === 'ok' && slices.length === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Monetization outcomes"
      icon="landmark"
      state={frameState}
      message={message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
    >
      <DonutChartWrapper title="Monetization outcomes" slices={slices} valueFormatter={(v) => formatMoney(String(v), currency)} />
    </WidgetFrame>
  )
}
